import { GridsterConfig } from 'angular-gridster2';
import { DashBoardInterface, GadGetInterface } from './interfaces/dashboard';

/**
 * Separação modelo-persistido × estado-runtime do dashboard.
 *
 * CONTRATO: DashBoardInterface[] é persistido em Mktpdashboard.EXPRESSION
 * e editado cru pelo "modo avançado" — toda mudança aqui precisa ser
 * compatível com dashboards já salvos em produção (só remoção de lixo
 * runtime e normalização defensiva; nenhuma chave é renomeada).
 *
 * Funções puras — testadas em dashboard-serializer.spec.ts.
 */

/**
 * Chaves do GridsterConfig que fazem sentido persistir.
 * `api` e callbacks NUNCA entram; qualquer chave desconhecida
 * (runtime futuro) também não vaza para o banco.
 */
export const PERSISTED_OPTION_KEYS: readonly string[] = [
    'setGridSize', 'gridType', 'compactType', 'displayGrid',
    'margin', 'outerMargin', 'outerMarginTop', 'outerMarginRight', 'outerMarginBottom', 'outerMarginLeft',
    'useTransformPositioning', 'mobileBreakpoint',
    'minCols', 'maxCols', 'minRows', 'maxRows',
    'maxItemCols', 'minItemCols', 'maxItemRows', 'minItemRows',
    'maxItemArea', 'minItemArea', 'defaultItemCols', 'defaultItemRows',
    'fixedColWidth', 'fixedRowHeight', 'rowHeightRatio',
    'keepFixedHeightInMobile', 'keepFixedWidthInMobile',
    'scrollSensitivity', 'scrollSpeed',
    'enableEmptyCellClick', 'enableEmptyCellContextMenu', 'enableEmptyCellDrop', 'enableEmptyCellDrag',
    'enableOccupiedCellDrop', 'emptyCellDragMaxCols', 'emptyCellDragMaxRows',
    'ignoreMarginInRow', 'draggable', 'resizable', 'swap', 'pushItems',
    'disablePushOnDrag', 'disablePushOnResize', 'pushDirections', 'pushResizeItems',
    'disableWindowResize', 'disableWarnings', 'scrollToNewItems',
];

function pickPersistedOptions(options: Record<string, unknown> | null | undefined): Record<string, unknown> {
    const clean: Record<string, unknown> = {};
    if (options == null || typeof options !== 'object') {
        return clean;
    }
    for (const key of PERSISTED_OPTION_KEYS) {
        if (options[key] !== undefined) {
            if (key === 'draggable' || key === 'resizable') {
                const sub = options[key] as Record<string, unknown> | null;
                clean[key] = { enabled: sub != null && typeof sub === 'object' ? !!sub['enabled'] : false };
            } else {
                clean[key] = options[key];
            }
        }
    }
    return clean;
}

/**
 * Prepara o dashboard para gravação: deep-clone SEM estado runtime
 * (options.api, callbacks, dataset do chart, flags transitórias).
 */
export function serializeDashboard(dashboard: DashBoardInterface[] | null | undefined): DashBoardInterface[] {
    if (!Array.isArray(dashboard)) {
        return [];
    }
    // Round-trip descarta funções (callbacks) e clona; 'api' sai via replacer
    // para não serializar referências de componente.
    const clone: any[] = JSON.parse(JSON.stringify(dashboard, (key, value) => (key === 'api' ? undefined : value)));

    for (const tab of clone) {
        if (tab == null || typeof tab !== 'object') {
            continue;
        }
        delete tab.active; // flag runtime que o antigo setActiveTab gravava no modelo
        tab.options = pickPersistedOptions(tab.options);
        tab.filter = Array.isArray(tab.filter) ? tab.filter : [];
        tab.gadGets = Array.isArray(tab.gadGets) ? tab.gadGets : [];
        for (const gadget of tab.gadGets) {
            // linhas retornadas pela QUERY nunca vão para o EXPRESSION;
            // charts SEM query podem ter data estática autoral — preservada
            if (gadget?.chart?.query != null && gadget?.chart?.options && 'data' in gadget.chart.options) {
                delete gadget.chart.options.data;
            }
        }
    }
    return clone as DashBoardInterface[];
}

function normalizeGadget(raw: unknown, index: number): GadGetInterface | null {
    if (raw == null || typeof raw !== 'object') {
        return null;
    }
    const gadget = raw as GadGetInterface;
    if (gadget.type !== 'minicard' && gadget.type !== 'chart' && gadget.type !== 'table') {
        return null;
    }
    if (gadget.gridsterItem == null || typeof gadget.gridsterItem !== 'object') {
        gadget.gridsterItem = { cols: 2, rows: 2, x: 0, y: 0 };
    }
    if (gadget.name == null) {
        gadget.name = `${gadget.type}_${index}`;
    }
    if (gadget.chart?.query != null && gadget.chart?.options && 'data' in (gadget.chart.options as object)) {
        // limpa dashboards antigos poluídos com dataset de query gravado
        delete (gadget.chart.options as Record<string, unknown>)['data'];
    }
    return gadget;
}

/**
 * Normalização defensiva no load: garante defaults para nós ausentes
 * (options/draggable/resizable/gadGets/filter), descarta lixo runtime
 * legado já gravado e aplica as options persistidas SOBRE o default
 * (absorve o antigo changeOptions do componente).
 *
 * @param defaults factory das options default (inclui callbacks vivos
 *                 do componente — por isso vem de fora).
 */
export function normalizeDashboard(
    raw: unknown,
    defaults: () => GridsterConfig
): DashBoardInterface[] {
    if (!Array.isArray(raw)) {
        return [];
    }

    const result: DashBoardInterface[] = [];
    for (let i = 0; i < raw.length; i++) {
        const tab = raw[i] as Partial<DashBoardInterface> & { active?: unknown } | null;
        if (tab == null || typeof tab !== 'object') {
            continue;
        }
        delete tab.active;

        const options = defaults();
        const persisted = tab.options as Record<string, unknown> | undefined;
        if (persisted != null && typeof persisted === 'object') {
            for (const key of PERSISTED_OPTION_KEYS) {
                if (persisted[key] === undefined) {
                    continue;
                }
                if (key === 'draggable' || key === 'resizable') {
                    (options as Record<string, unknown>)[key] = {
                        ...(options as Record<string, any>)[key],
                        ...(persisted[key] as object),
                    };
                } else {
                    (options as Record<string, unknown>)[key] = persisted[key];
                }
            }
        }
        options.draggable = options.draggable ?? { enabled: false };
        options.resizable = options.resizable ?? { enabled: false };

        // Spread primeiro: chaves desconhecidas do JSON persistido
        // (ex.: showToolbar, campos futuros) são PRESERVADAS; só as
        // conhecidas são normalizadas por cima.
        result.push({
            ...(tab as DashBoardInterface),
            iddashboard: typeof tab.iddashboard === 'number' ? tab.iddashboard : -1,
            tabName: tab.tabName != null && `${tab.tabName}`.length > 0 ? `${tab.tabName}` : `tab_${i}_${Math.abs(Date.now() % 1000000)}`,
            tabTitle: tab.tabTitle ?? 'Aba',
            filter: Array.isArray(tab.filter) ? tab.filter : [],
            gadGets: (Array.isArray(tab.gadGets) ? tab.gadGets : [])
                .map((g, gi) => normalizeGadget(g, gi))
                .filter((g): g is GadGetInterface => g != null),
            options,
        });
    }
    return result;
}
